import React, { useEffect, useRef, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import { useParams } from "react-router-dom";
import categoryAPI from "../../api/categoryApi";
import AsyncSelect from "react-select/async";
import SEOFields from "../../components/partials/SEOFields";
import ImageUploader from "../../components/partials/ImageUploader";
import slugify from "slugify";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SunEditor from "suneditor-react";

function Edit() {
  const location = useParams();
  const id = location.id || {};

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [categoryTree, setCategoryTree] = useState([]);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [finalCategory, setFinalCategory] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const editorRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    shortDescription: "",
    slug: "",
    image: "",
    parentId: "",
    navbar: false,
    homepage: false,
  });

  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    tags: [],
  });

  useEffect(() => {
    fetchSingleDetails();
  }, []);

  const fetchSingleDetails = async () => {
    try {
      const res = await categoryAPI.getCategoryDetails(id);

      if (res.code === 200) {
        const result = res.data;

        setFormData({
          image: result?.image || "",
          name: result?.name || "",
          homepage: result?.showOnHomepage || false,
          navbar: result?.showInNavbar || false,
          description: result?.description || "",
          parentId: result?.parentId || "",
          shortDescription: result?.shortDescription || "",
        });

        setOriginalData({
          image: result.image || "",
          name: result.name || "",
          description: result.description || "",
          slug: result.slug || "",
          parentId: result.parentId || "",
          homepage: result.showOnHomepage || false,
          navbar: result.showInNavbar || false,
          shortDescription: result.shortDescription || "",
        });

        setSeoData({
          metaTitle: result?.meta?.title || "",
          metaDescription: result?.meta?.description || "",
          tags: result?.meta?.keywords
            ? result?.meta?.keywords?.split(",").map((tag) => tag.trim())
            : [],
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  useEffect(() => {
    categoryAPI.getListforDropdown().then((res) => {
      if (res.code === 200) {
        setCategoryTree(res.data);
        setVisibleOptions(res.data); // level 0 only
      }
    });
  }, []);


  useEffect(() => {
  if (categoryTree.length && formData.parentId) {
    const findPath = (tree, targetId, path = []) => {
      for (let item of tree) {
        const newPath = [...path, item];
        if (item._id === targetId) return newPath;
        if (item.children) {
          const res = findPath(item.children, targetId, newPath);
          if (res) return res;
        }
      }
      return null;
    };

    const path = findPath(categoryTree, formData.parentId);
    if (path) {
      setSelectedPath(path);
      setVisibleOptions(path[path.length - 1]?.children || []);
    }
  }
}, [categoryTree, formData.parentId]);

  const handleSelect = (item) => {
    const newPath = [...selectedPath, item];
    setSelectedPath(newPath);

    // ✅ ALWAYS set parentId
    setFormData((prev) => ({
      ...prev,
      parentId: item._id,
    }));

    if (item.children && item.children.length > 0) {
      setVisibleOptions(item.children);
    } else {
      setFinalCategory(item);
      setIsOpen(false);
    }
  };

  const selectedLabel = selectedPath.map((i) => i.name).join(" → ");

  const flattenCategories = (categories, prefix = "") => {
    return categories.reduce((acc, category) => {
      const label = prefix ? `${prefix} > ${category.label}` : category.label;

      acc.push({
        label,
        value: category._id,
        slug: category.slug,
      });

      if (category.children && category.children.length > 0) {
        const childOptions = flattenCategories(category.children, label);
        acc = acc.concat(childOptions);
      }

      return acc;
    }, []);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name field is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        // empty / null image ignore
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const fullPayload = {
        ...formData,
        metaTitle: seoData.metaTitle,
        metaDescription: seoData.metaDescription,
        keywords: seoData.tags.join(","),
      };

      const payload = getChangedFields(originalData, fullPayload);

      if (Object.keys(payload).length === 0) {
        showError("No changes detected");
        setIsLoading(false);
        hideLoader();
        return;
      }

      const res = await categoryAPI.updateDetails(id, payload);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add a New Category</h4>
            <h6>
              Create a new category to engage customers and drive store
              visibility.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Create New Category</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Category Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="categories"
              />

              <div className="row mt-3">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Parent Category</label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-home"></i>
                      </span>

                      <div
                        className="position-relative"
                        style={{ width: "100%" }}
                      >
                        <div
                          className="form-control"
                          onClick={() => setIsOpen(!isOpen)}
                          style={{ cursor: "pointer" }}
                        >
                          {selectedLabel || "Select Category"}
                        </div>

                        {isOpen && (
                          <div
                            className="border bg-white shadow position-absolute w-100"
                            style={{ zIndex: 10 }}
                          >
                            {visibleOptions.map((item) => (
                              <div
                                key={item._id}
                                onClick={() => handleSelect(item)}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                {item.name}
                                {item.children?.length > 0 && " ›"}
                              </div>
                            ))}

                            {selectedPath.length > 0 && (
                              <div
                                onClick={() => {
                                  const parentPath = selectedPath.slice(0, -1);
                                  setSelectedPath(parentPath);
                                  const parent =
                                    parentPath.length === 0
                                      ? categoryTree
                                      : parentPath[parentPath.length - 1]
                                          .children;
                                  setVisibleOptions(parent);
                                }}
                                className="p-2 text-primary"
                                style={{ cursor: "pointer" }}
                              >
                                ← Back
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.selectedStore && (
                      <span className="errors text-danger">
                        {errors.selectedStore}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-pencil"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Title"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Show In Navbar</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="navbar"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={formData.navbar}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Show in Home Page</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="homepage"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={formData.homepage}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">Short Description</label>
                  <textarea
                    className="form-control"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>

                  <SunEditor
                    getSunEditorInstance={(sunEditor) => {
                      editorRef.current = sunEditor;
                    }}
                    height="250px"
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["font", "fontSize"],
                        ["formatBlock"],
                        ["bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor"],
                        ["align", "list", "table"],
                        ["link", "image", "video"],
                        ["fullScreen", "preview", "print"],
                        ["codeView"],
                      ],
                      allowedClassNames: ".*", // sab classes allow
                      attributesWhitelist: {
                        all: "style|class|id",
                      },
                      addTagsWhitelist: ".*", // extra tags remove na kare
                      imageFileInput: true,
                      font: [
                        "Arial",
                        "Comic Sans MS",
                        "Courier New",
                        "Georgia",
                        "Tahoma",
                        "Trebuchet MS",
                        "Verdana",
                      ],
                      fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                    }}
                    setContents={formData.description}
                    onChange={handleEditorChange}
                  />
                </div>
              </div>

              <hr />

              <SEOFields
                seoData={seoData}
                setSeoData={setSeoData}
                slug={slugify(formData.name || "", { lower: true })}
                errors={errors}
              />

              <div className="row">
                <div className="col-12 d-flex justify-content-end mt-3">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
